import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ReviewModal = ({ submission, task, onClose, onReviewed }) => {
  const [score, setScore] = useState(submission?.score ?? '');
  const [remarks, setRemarks] = useState(submission?.mentorRemarks ?? '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const trainee = submission?.trainee;
  const maxScore = task?.maxScore || 100;

  const handleSubmit = async () => {
    if (score === '' || score === null) { setError('Score is required'); return; }
    if (Number(score) < 0 || Number(score) > maxScore) {
      setError(`Score must be between 0 and ${maxScore}`);
      return;
    }
    setLoading(true);
    setError('');
    try {
      await onReviewed(submission.submissionId, { score: Number(score), mentorRemarks: remarks });
      onClose();
    } catch {
      setError('Failed to save review. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-card rounded-xl shadow-xl w-full max-w-lg mx-4">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Review Submission</h2>
            <p className="text-sm text-muted-foreground mt-0.5">
              {trainee?.firstname} {trainee?.lastname} — {task?.title}
            </p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-2xl leading-none">&times;</button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Submission</label>
            <div className="bg-muted/40 rounded-lg p-4 text-sm text-foreground whitespace-pre-wrap min-h-[80px]">
              {submission?.submissionText || <span className="text-muted-foreground italic">No text submitted</span>}
            </div>
            {submission?.fileUrl && (
              <a href={submission.fileUrl} target="_blank" rel="noreferrer"
                className="text-xs text-primary mt-1 block hover:underline">
                View attached file
              </a>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Score * <span className="text-muted-foreground font-normal">(max {maxScore})</span>
              </label>
              <Input
                type="number"
                value={score}
                onChange={e => setScore(e.target.value)}
                min={0}
                max={maxScore}
                placeholder={`0 – ${maxScore}`}
              />
            </div>
            <div className="flex items-end pb-1">
              {score !== '' && (
                <span className={`text-2xl font-bold ${Number(score) >= maxScore * 0.7 ? 'text-success' : Number(score) >= maxScore * 0.4 ? 'text-warning' : 'text-error'}`}>
                  {Math.round((Number(score) / maxScore) * 100)}%
                </span>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Mentor Remarks</label>
            <textarea
              className="w-full border border-border rounded-lg p-3 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              rows={3}
              value={remarks}
              onChange={e => setRemarks(e.target.value)}
              placeholder="Feedback for the trainee..."
            />
          </div>

          {error && <p className="text-sm text-error">{error}</p>}
        </div>

        <div className="flex justify-end gap-3 p-6 border-t border-border">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? 'Saving...' : 'Save Review'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ReviewModal;
